import { type FC, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import { Image } from '@chakra-ui/react';
import { Button, Typography, View } from '@shinetools/sunshine-universal';

import fadedFlowerImg from 'assets/brand/faded-flower@2x.png';
import SunshineModal from 'components/_core/SunshineModal';
import { type ClearAllDigitsFn } from 'components/PasscodeInput/types';
import { useUser } from 'helpers/auth';
import logger from 'helpers/logger';

import { ChallengeStateName } from '../../../../__generated__/GQL';
import RichText from '../../../_core/RichText';
import PasscodeInput from '../../../PasscodeInput';
import useToast from '../../../ToastProvider/useToast';
import { VerifyChallengeDocument } from '../../graphql/mutations/verifyChallenge.gql';
import { useVerifySmsSCAOperation } from '../../hooks/useVerifySmsSCAOperation';
import { type StrongAuthenticationChallengeChildModalProps } from '../../utils/types';
import locales from './locales';

const DEFAULT_TIMER = 180;

const SmsChallenge: FC<StrongAuthenticationChallengeChildModalProps> = ({
  challengeId,
  executeStrongAuthenticationOperation,
  isOpen,
  onClose,
  payload,
}) => {
  const [error, setError] = useState<unknown | null>(null);
  const [timer, setTimer] = useState(DEFAULT_TIMER);
  const [isExpired, setIsExpired] = useState(false);
  const [wrongCode, setWrongCode] = useState(false);
  const { showToast } = useToast();
  const intl = useIntl();

  const user = useUser();
  const { phone } = user;

  const { canRetry, isRequesting, isSmsVerifying, verifySmsOperation } =
    useVerifySmsSCAOperation({
      executeStrongAuthenticationOperation,
      payload,
    });

  // Hook executed when the user clicks on the confirm button
  const onSmsCodeSubmit = useCallback(
    async (code: string, clearAllDigits: ClearAllDigitsFn) => {
      try {
        if (!isSmsVerifying && !isRequesting) {
          const verificationResult = await verifySmsOperation({
            challengeId,
            name: ChallengeStateName.ChallengeApproved,
            verificationCode: code,
          });

          if (verificationResult === false) {
            setWrongCode(true);
          }

          clearAllDigits({ andFocusFirstInput: true });
        }
      } catch (err) {
        setError(locales.unknownError);
      }
    },
    [isSmsVerifying, isRequesting, verifySmsOperation, challengeId],
  );

  const [verifyChallenge] = useMutation(VerifyChallengeDocument);

  const declineChallengeAndClose = async () => {
    try {
      await verifyChallenge({
        variables: {
          input: {
            challengeId,
            name: ChallengeStateName.ChallengeDeclined,
            verificationCode: '',
          },
        },
      });
    } catch (err) {
      logger.log(err);
    } finally {
      onClose(true);
    }
  };

  const onNotRetriable = useCallback(async () => {
    try {
      await verifyChallenge({
        variables: {
          input: {
            challengeId,
            name: ChallengeStateName.ChallengeFailedTooManyAttempts,
            verificationCode: '',
          },
        },
      });
      showToast({
        message: intl.formatMessage({
          id: 'authentication.challenge.sms.max_attempts',
        }),
        type: 'error',
      });
    } catch (err) {
      logger.log(err);
    } finally {
      onClose(false);
    }
  }, [challengeId, showToast, intl, onClose, verifyChallenge]);

  useEffect(() => {
    if (isOpen) {
      const interval = setInterval(
        () =>
          setTimer((previousTimer) => {
            if (previousTimer === 0) {
              setIsExpired(true);
              return previousTimer;
            }

            return previousTimer - 1;
          }),
        1000,
      );

      return () => {
        clearInterval(interval);
      };
    }

    return () => {};
  }, [isOpen, setIsExpired]);

  if (!canRetry) {
    onNotRetriable();
    return null;
  }

  const sendToInfo = phone
    ? locales.formatString(locales.sentToSingular, { phone })
    : '';

  const timerText =
    timer > 1
      ? locales.formatString(locales.expiresInPlural, {
          timer,
        })
      : locales.formatString(locales.expiresInSingular, {
          timer,
        });
  if (error) {
    return (
      <SunshineModal isOpen={isOpen} onClose={() => onClose(true)}>
        <SunshineModal.Footer>
          <Typography.Text marginTop="$space.16" variant="error">
            <FormattedMessage id="authentication.challenge.unknown_error" />
          </Typography.Text>
        </SunshineModal.Footer>
      </SunshineModal>
    );
  }

  return (
    <SunshineModal isOpen={isOpen} onClose={declineChallengeAndClose}>
      <SunshineModal.Header />
      <SunshineModal.Body display="block">
        <SunshineModal.Title>{locales.title}</SunshineModal.Title>
        {isExpired ? (
          <>
            <Image
              alt={locales.codeExpired}
              marginTop="$space.16"
              marginX="auto"
              src={fadedFlowerImg}
              width="180px"
            />
            <Typography.Text marginTop="$space.8" variant="error">
              {locales.codeExpired}
            </Typography.Text>
          </>
        ) : (
          <>
            <Typography.Text marginTop="$space.8">
              {locales.description}
            </Typography.Text>

            <View backgroundColor="transparent" marginVertical="$space.32">
              <PasscodeInput
                disabled={!canRetry}
                length={6}
                onSubmit={onSmsCodeSubmit}
              />
              {wrongCode && canRetry ? (
                <Typography.Text marginTop="$space.16" variant="error">
                  <FormattedMessage id="authentication.challenge.sms.wrong_code" />
                </Typography.Text>
              ) : null}
            </View>
            <View alignItems="center">
              {canRetry && sendToInfo != '' ? (
                <RichText size="small">{sendToInfo}</RichText>
              ) : null}
              {canRetry ? (
                <Typography.Text size="small">{timerText}</Typography.Text>
              ) : null}
            </View>
          </>
        )}
      </SunshineModal.Body>
      {!isExpired ? (
        <SunshineModal.Footer>
          <Button
            isDisabled={!canRetry}
            isLoading={isSmsVerifying || isRequesting}
            onPress={() => {
              onClose(false);
            }}
            variant="primary"
          >
            {locales.confirm}
          </Button>
        </SunshineModal.Footer>
      ) : null}
    </SunshineModal>
  );
};

export default SmsChallenge;
